import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  fetchCompaniesAggregated,
  refreshAllCredentialsApi,
  refreshCredentialsByBankApi,
  runAccountReconciliation,
  syncAccountSapEntries,
  type AccountWithSap,
  type CompanyAggregate,
  type RefreshAllResponse,
} from "../api";
import { formatDateEs, formatIban, formatMoney, subtypeLabel } from "../lib/grouping";

export function CompaniesAggregated() {
  const navigate = useNavigate();
  const [items, setItems] = useState<CompanyAggregate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastBatch, setLastBatch] = useState<RefreshAllResponse | null>(null);
  // Broadcast de "Expandir todo / Colapsar todo": incrementamos `revision`
  // cada pulsación para que cada CompanyCard adopte `state` aunque coincida
  // con su estado local actual.
  const [bulkExpandState, setBulkExpandState] = useState<boolean | null>(null);
  const [bulkExpandRevision, setBulkExpandRevision] = useState(0);
  const expandAll = () => {
    setBulkExpandState(true);
    setBulkExpandRevision((r) => r + 1);
  };
  const collapseAll = () => {
    setBulkExpandState(false);
    setBulkExpandRevision((r) => r + 1);
  };

  const load = async () => {
    setError(null);
    try {
      const r = await fetchCompaniesAggregated();
      setItems(r.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onRefreshAll = async () => {
    if (refreshing) return;
    if (!confirm("¿Refrescar todas las credenciales bancarias ahora? Se descargan saldos y movimientos de todas las cuentas en serie; puede tardar varios minutos.")) {
      return;
    }
    setRefreshing(true);
    setError(null);
    setLastBatch(null);
    try {
      const r = await refreshAllCredentialsApi();
      setLastBatch(r);
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      <section className="card">
        <div className="card-head">
          <div>
            <h2>Resumen por empresa</h2>
            <p className="meta">
              Saldo consolidado por empresa sumando todas sus cuentas y
              préstamos. Los balances son los del último payload sincronizado.
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              type="button"
              onClick={onRefreshAll}
              disabled={refreshing}
              title="Refresca todas las credenciales bancarias y recarga los balances."
            >
              {refreshing ? "Actualizando…" : "↻ Actualizar cuentas"}
            </button>
            <button type="button" onClick={load} disabled={refreshing}>
              Recargar
            </button>
            <button
              type="button"
              onClick={expandAll}
              title="Mostrar las cuentas y préstamos de TODAS las empresas."
              disabled={!items || items.length === 0}
            >
              ▼ Expandir todo
            </button>
            <button
              type="button"
              onClick={collapseAll}
              title="Ocultar las cuentas y préstamos de TODAS las empresas."
              disabled={!items || items.length === 0}
            >
              ▶ Colapsar todo
            </button>
            <button
              type="button"
              onClick={() => navigate("/resumen/imprimir")}
              title="Ver un resumen imprimible con las cuentas de todas las empresas."
            >
              🖨 Imprimir
            </button>
          </div>
        </div>
        {lastBatch && (
          <div className={lastBatch.failed === 0 ? "success" : "warning"}>
            {lastBatch.failed === 0 ? "✅" : "⚠️"} Refresco completado:
            {" "}OK <code>{lastBatch.ok}</code> · KO <code>{lastBatch.failed}</code> · total <code>{lastBatch.total}</code>
            {" "}en <code>{Math.round(lastBatch.durationMs / 1000)}s</code>
          </div>
        )}
        {error && <p className="error">{error}</p>}
        {items === null && !error && <p>Cargando…</p>}
        {items && items.length === 0 && (
          <p className="meta">
            No hay empresas. Crea alguna desde la pestaña "Empresas" y da de alta sus cuentas en "Cuentas".
          </p>
        )}
      </section>

      {/*
        Grid responsivo: cada tarjeta colapsada ocupa una celda
        (min 480px). Cuando la empresa se expande sus cuentas y préstamos,
        aplica gridColumn: 1 / -1 desde el propio CompanyCard para ocupar
        todo el ancho — así la tabla no queda apretada.
      */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(480px, 1fr))",
          gap: "0.4rem",
          marginTop: "0.4rem",
        }}
      >
        {(items ?? []).map((c) => (
          <CompanyCard
            key={c.id || c.name}
            company={c}
            onRefreshed={load}
            bulkExpandState={bulkExpandState}
            bulkExpandRevision={bulkExpandRevision}
          />
        ))}
      </div>
    </>
  );
}

/**
 * Devuelve el saldo bancario efectivo a comparar con SAP y a sumar en el
 * total por empresa. Debe coincidir con `effectiveBalance` del backend
 * ([backend/src/lib/aggregateCompanies.ts](../../../c%3A/VsCode/ConciliaEcilimp/backend/src/lib/aggregateCompanies.ts)).
 *
 * Póliza de crédito: prefiere current (dispuesto real, negativo); si no,
 * cae al balance del último movimiento (que en pólizas suele traer el
 * dispuesto real). Nunca usa available para pólizas (available = línea
 * libre, no es el saldo económico).
 *
 * Cuenta normal: current si !== null && !== 0, si no available.
 */
export function pickBankBalance(
  current: number | null,
  available: number | null,
  isCreditLine: boolean = false,
  lastMovementBalance: number | null = null,
): number | null {
  if (isCreditLine) {
    if (current !== null && current !== 0) return current;
    if (lastMovementBalance !== null) return lastMovementBalance;
    return null;
  }
  if (current !== null && current !== 0) return current;
  if (available !== null) return available;
  return current;
}

function CompanyCard({
  company,
  onRefreshed,
  bulkExpandState,
  bulkExpandRevision,
}: {
  company: CompanyAggregate;
  onRefreshed: () => void;
  /** Estado forzado por los botones "Expandir todo / Colapsar todo" del padre.
   *  Se aplica cuando cambia `bulkExpandRevision`, ignorando el estado local. */
  bulkExpandState: boolean | null;
  bulkExpandRevision: number;
}) {
  const hasSap = company.hasSap;
  const totalEntries = Object.entries(company.totalsByCurrency);
  const displayName = company.alias || company.name;
  const accountsActive = company.accounts.filter((a) => a.active);
  const loansActive = company.loans.filter((l) => l.active);

  const [sapBusy, setSapBusy] = useState(false);
  const [sapMsg, setSapMsg] = useState<string | null>(null);
  const [sapErrors, setSapErrors] = useState<Array<{ iban: string; alias: string; bankCode: string; error: string }>>([]);
  const [recBusy, setRecBusy] = useState(false);
  const [recMsg, setRecMsg] = useState<string | null>(null);
  const [recErrors, setRecErrors] = useState<Array<{ iban: string; alias: string; error: string }>>([]);
  const pollRef = useRef<number | null>(null);
  // Toggle mostrar/ocultar detalle de la empresa. Recordamos la preferencia
  // por empresa en localStorage para que sobreviva a recargas del navegador.
  // Default: colapsado — el usuario abre solo las que necesita revisar.
  const storageKey = `resumen.expanded.${company.id || company.name}`;
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === "0") return false;
      if (raw === "1") return true;
    } catch { /* ignore */ }
    return false; // default colapsado
  });
  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKey, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };
  // Reaccionar a los botones "Expandir todo / Colapsar todo" de la barra
  // superior. Cada pulsación incrementa `bulkExpandRevision`; cuando cambia,
  // adoptamos el estado forzado y actualizamos localStorage.
  useEffect(() => {
    if (bulkExpandState === null || bulkExpandRevision === 0) return;
    setExpanded(bulkExpandState);
    try {
      localStorage.setItem(storageKey, bulkExpandState ? "1" : "0");
    } catch { /* ignore */ }
    // Deliberadamente NO incluimos storageKey/bulkExpandState en deps: solo
    // queremos reaccionar cuando la revisión cambia (nuevas pulsaciones del
    // usuario), no cuando el propio companyId cambie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkExpandRevision]);

  useEffect(() => {
    return () => {
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
    };
  }, []);

  /** Lanza el sync SAP para todas las cuentas activas de la empresa y hace
   *  polling recargando el aggregate hasta que todas las cuentas tienen su
   *  `sapEntriesLastSyncedAt` posterior al `startedAt` inicial. */
  const doSyncSap = async () => {
    if (accountsActive.length === 0 || sapBusy) return;
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setSapBusy(true);
    setSapMsg(null);
    setSapErrors([]);
    const startedAtIso = new Date().toISOString();
    const ibans = new Set(accountsActive.map((a) => a.iban));
    try {
      const results = await Promise.allSettled(
        accountsActive.map((a) => syncAccountSapEntries(a.iban)),
      );
      const failedStart = results.filter((r) => r.status === "rejected").length;
      if (failedStart === accountsActive.length) {
        setSapMsg(`❌ No se pudo arrancar ningún sync (${failedStart} fallidos)`);
        setSapBusy(false);
        return;
      }
      setSapMsg(`⏳ Sync SAP en curso… 0/${accountsActive.length} cuentas terminadas.`);
      // Polling cada 3s: recargamos el aggregate y contamos cuántas cuentas
      // tienen ya un lastSyncedAt >= startedAtIso.
      pollRef.current = window.setInterval(async () => {
        try {
          const fresh = await fetchCompaniesAggregated();
          const freshCompany = fresh.items.find((c) => c.id === company.id);
          if (!freshCompany) return;
          const freshAccounts = freshCompany.accounts.filter((a) => ibans.has(a.iban));
          const done = freshAccounts.filter(
            (a) => a.sapEntriesLastSyncedAt !== null && a.sapEntriesLastSyncedAt >= startedAtIso,
          );
          const errored = done.filter((a) => a.sapEntriesLastError);
          if (done.length >= accountsActive.length) {
            if (pollRef.current !== null) {
              window.clearInterval(pollRef.current);
              pollRef.current = null;
            }
            setSapErrors(
              errored.map((a) => ({
                iban: a.iban,
                alias: a.alias,
                bankCode: a.bankCode,
                error: a.sapEntriesLastError ?? "(sin detalle)",
              })),
            );
            setSapMsg(
              errored.length === 0
                ? `✅ Sync SAP terminado. ${done.length}/${accountsActive.length} cuentas actualizadas.`
                : `⚠️ Sync SAP terminado. ${done.length - errored.length}/${done.length} OK · ${errored.length} con error:`,
            );
            setSapBusy(false);
            // Actualiza toda la vista al terminar.
            void onRefreshed();
          } else {
            setSapMsg(`⏳ Sync SAP en curso… ${done.length}/${accountsActive.length} cuentas terminadas.`);
          }
        } catch (e: unknown) {
          console.warn("[sap-progress] polling error:", e);
        }
      }, 3000);
    } catch (e: unknown) {
      setSapMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
      setSapBusy(false);
    }
  };

  /** Ejecuta el matcher (1:1, mismo signo, importe exacto, ±7 días) para
   *  las cuentas activas de la empresa, UNA A UNA (secuencial). Va
   *  actualizando el mensaje de progreso "i/N" y al terminar refresca la
   *  vista para que los saldos reflejen el nuevo estado. */
  const doReconcile = async () => {
    if (accountsActive.length === 0 || recBusy) return;
    setRecBusy(true);
    setRecMsg(`⏳ Conciliando 0/${accountsActive.length} cuentas…`);
    setRecErrors([]);
    let totalNew = 0;
    let totalMatches = 0;
    let totalPendingBank = 0;
    let totalPendingSap = 0;
    const errored: Array<{ iban: string; alias: string; error: string }> = [];
    for (let i = 0; i < accountsActive.length; i++) {
      const acc = accountsActive[i]!;
      setRecMsg(
        `⏳ Conciliando ${i + 1}/${accountsActive.length}… (${acc.alias || acc.bankCode})`,
      );
      try {
        const r = await runAccountReconciliation(acc.iban);
        totalNew += r.newAutoMatches;
        totalMatches += r.totalMatches;
        totalPendingBank += r.pendingMovements;
        totalPendingSap += r.pendingSapEntries;
      } catch (e: unknown) {
        errored.push({
          iban: acc.iban,
          alias: acc.alias,
          error: e instanceof Error ? e.message : String(e),
        });
      }
    }
    const okCount = accountsActive.length - errored.length;
    if (errored.length === 0) {
      setRecMsg(
        `✅ Conciliación terminada. ${okCount}/${accountsActive.length} cuentas · ` +
          `${totalNew} emparejamiento${totalNew === 1 ? "" : "s"} nuevo${totalNew === 1 ? "" : "s"} · ` +
          `total conciliados ${totalMatches} · pendientes ${totalPendingBank} banco / ${totalPendingSap} SAP.`,
      );
    } else {
      setRecMsg(
        `⚠️ Conciliación terminada. ${okCount}/${accountsActive.length} OK · ` +
          `${errored.length} con error:`,
      );
      setRecErrors(errored);
    }
    void onRefreshed();
    setRecBusy(false);
  };

  // Botón compacto uniforme para "Sync SAP", "Conciliar" y el chevron.
  const headerBtnStyle: React.CSSProperties = {
    padding: "0.15rem 0.55rem",
    height: "1.7rem",
    fontSize: "0.82em",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
  };

  return (
    <section
      className="card owner-card"
      style={{
        padding: "0.55rem 0.9rem",
        marginTop: 0,
        // Grid del padre: cuando la empresa está expandida ocupa TODAS las
        // columnas (para que la tabla no quede apretada). Cuando está
        // colapsada, ocupa una celda del grid responsivo (min 480px).
        gridColumn: expanded ? "1 / -1" : "auto",
      }}
    >
      <div className="card-head" style={{ marginBottom: 0, alignItems: "center" }}>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <button
            type="button"
            onClick={toggleExpanded}
            title={expanded ? "Ocultar cuentas y préstamos" : "Mostrar cuentas y préstamos"}
            aria-expanded={expanded}
            style={{
              width: "1.5rem",
              height: "1.5rem",
              minWidth: "1.5rem",
              padding: 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75em",
            }}
          >
            {expanded ? "▼" : "▶"}
          </button>
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
            <h3
              style={{ margin: 0, cursor: "pointer", fontSize: "0.95em" }}
              onClick={toggleExpanded}
            >
              {company.isCompany ? "🏢" : "👤"} {displayName}
              {company.alias && (
                <span className="meta" style={{ marginLeft: "0.4rem", fontSize: "0.82em", fontWeight: 400 }}>
                  · {company.name}
                </span>
              )}
            </h3>
            <span className="meta" style={{ fontSize: "0.78em" }}>
              {company.id && <>NIF <code>{company.id}</code> · </>}
              {company.bankCodes.length} {company.bankCodes.length === 1 ? "banco" : "bancos"} ·{" "}
              {accountsActive.length} {accountsActive.length === 1 ? "cta" : "ctas"}
              {loansActive.length > 0 && <> · {loansActive.length} pmos</>}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.35rem", alignItems: "center" }}>
          {hasSap && (
            <button
              type="button"
              onClick={doSyncSap}
              disabled={sapBusy || accountsActive.length === 0}
              title="Sincroniza los apuntes contables SAP de todas las cuentas de esta empresa en background."
              style={headerBtnStyle}
            >
              {sapBusy ? "…" : "↻ Sync SAP"}
            </button>
          )}
          {hasSap && (
            <button
              type="button"
              onClick={doReconcile}
              disabled={recBusy || accountsActive.length === 0}
              title="Concilia movimientos banco ↔ apuntes SAP para cada cuenta de esta empresa, una a una (1:1, mismo signo, importe exacto, ±7 días)."
              style={headerBtnStyle}
            >
              {recBusy ? "…" : "🔗 Conciliar"}
            </button>
          )}
          <div className="owner-totals">
            {totalEntries.map(([cur, t]) => (
              <span key={cur} className="kpi-mini" style={{ fontSize: "0.82em", padding: "0.1rem 0.55rem" }}>
                {formatMoney(t.available, cur)}
              </span>
            ))}
          </div>
        </div>
      </div>
      {sapMsg && (
        <p className={sapMsg.startsWith("❌") ? "error" : "meta"}>{sapMsg}</p>
      )}
      {recMsg && (
        <p className={recMsg.startsWith("❌") ? "error" : "meta"}>{recMsg}</p>
      )}
      {recErrors.length > 0 && (
        <ul style={{ margin: "0 0 0.5rem 1.5rem", fontSize: "0.85em" }}>
          {recErrors.map((e) => (
            <li key={e.iban} style={{ marginBottom: "0.2rem" }}>
              <Link to={`/cuentas/${encodeURIComponent(e.iban)}/conciliar`} state={{ from: "/resumen" }}>
                <code>{formatIban(e.iban)}</code>
              </Link>
              {e.alias && (
                <>
                  {"  · "}
                  {e.alias}
                </>
              )}
              {" — "}
              <span className="error">{e.error}</span>
            </li>
          ))}
        </ul>
      )}
      {sapErrors.length > 0 && (
        <ul style={{ margin: "0 0 0.5rem 1.5rem", fontSize: "0.85em" }}>
          {sapErrors.map((e) => (
            <li key={e.iban} style={{ marginBottom: "0.2rem" }}>
              <Link to={`/cuentas/${encodeURIComponent(e.iban)}/apuntes-sap`}>
                <code>{formatIban(e.iban)}</code>
              </Link>
              {"  · "}
              <span className="meta">{e.bankCode}</span>
              {e.alias && (
                <>
                  {"  · "}
                  {e.alias}
                </>
              )}
              {" — "}
              <span className="error">{e.error}</span>
            </li>
          ))}
        </ul>
      )}

      {expanded && accountsActive.length > 0 && (
        <table className="data accounts compact">
          <thead>
            <tr>
              <th>Banco</th>
              <th>IBAN</th>
              <th>Alias</th>
              <th className="num">Disponible</th>
              <th className="num">Saldo SAP</th>
              <th>Estado</th>
              <th>Últ. sync</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {accountsActive.map((a) => (
              <AccountRow key={a.iban} account={a} onRefreshed={onRefreshed} />
            ))}
          </tbody>
        </table>
      )}

      {expanded && loansActive.length > 0 && (
        <>
          <h4>Préstamos</h4>
          <table className="data loans">

            <thead>
              <tr>
                <th>Banco</th>
                <th>Contrato</th>
                <th>Alias</th>
                <th className="num">Pendiente</th>
                <th>Vencimiento</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loansActive.map((l) => (
                <tr key={`${l.bankCode}::${l.contractNumber}`}>
                  <td><code>{l.bankCode}</code></td>
                  <td><code>{l.contractNumber}</code></td>
                  <td>{l.alias || <span className="meta">—</span>}</td>
                  <td className="num">
                    {l.lastPendingAmount !== null ? formatMoney(l.lastPendingAmount, l.currency) : <span className="meta">—</span>}
                  </td>
                  <td>{l.lastExpirationDate || <span className="meta">—</span>}</td>
                  <td className="actions">
                    <Link
                      to={`/prestamos/${encodeURIComponent(l.bankCode)}/${encodeURIComponent(l.contractNumber)}/amortizacion`}
                    >
                      <button type="button">Cuadro</button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function AccountRow({
  account,
  onRefreshed,
}: {
  account: AccountWithSap;
  onRefreshed: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const doRefresh = async () => {
    if (busy) return;
    setBusy(true);
    setRowError(null);
    try {
      // Pasamos el IBAN para que el backend use el índice inverso
      // (`knownAccountIbans`) y refresque solo la credencial que ya trajo
      // esta cuenta. Evita el problema de ráfaga a Bankinter empresa (9
      // credenciales sin pausa → success:true vacío). Si el índice no está
      // poblado (primera vez), el backend cae al comportamiento anterior.
      const r = await refreshCredentialsByBankApi(account.bankCode, account.iban);
      if (r.failed > 0) {
        const firstErr = r.results.find((x) => !x.ok);
        setRowError(
          firstErr
            ? `Refresh KO (${r.failed}/${r.total}): status=${firstErr.status} code=${firstErr.code}${firstErr.message ? " · " + firstErr.message : ""}`
            : `Refresh KO (${r.failed}/${r.total})`,
        );
      }
      onRefreshed();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("404")) {
        setRowError(`No hay credencial guardada para el banco "${account.bankCode}". Conéctalo primero desde "Conectar banco".`);
      } else {
        setRowError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  // Estado de cuadre entre saldo banco y saldo SAP.
  //  - "cuadrado" cuando ambos existen y difieren ≤ 0.01 €.
  //  - "conciliado hasta <fecha>" cuando ambos existen pero difieren y hay
  //    balanceSealDate.
  //  - "descuadrado" si no hay fecha conciliada.
  //  - "sin apuntes SAP" cuando no se ha sincronizado la cuenta contable.
  //  - "—" cuando falta el saldo del banco (todavía no se ha sincronizado).
  //
  // El saldo bancario que se compara con SAP es `lastBalanceCurrent` (saldo
  // actual). En cuentas corrientes normales coincide con `available`, pero en
  // pólizas de crédito difieren: `available` es el CRÉDITO DISPONIBLE que
  // queda de la línea (positivo, cae al usar) y `current` es el saldo
  // dispuesto (típicamente negativo). La contabilidad refleja el `current`.
  //
  // Fallback defensivo: si `current` es null (conector no lo envía) O es 0
  // mientras `available` no es 0 (dato residual de cuentas sincronizadas
  // antes del fix del backend que ahora guarda null), usamos `available`.
  // No es perfecto (una cuenta legítimamente a cero con crédito disponible
  // caería aquí), pero cubre el caso mayoritario de cuentas corrientes de
  // Bankinter/Cajamar/Caixabank donde WR no manda `current`.
  const bankBal = pickBankBalance(
    account.lastBalanceCurrent,
    account.lastBalanceAvailable,
    account.isCreditLine,
    account.lastMovementBalance,
  );
  const sapBal = account.sapBalance;
  // La cuenta se pinta como póliza si el usuario la marcó explícitamente en
  // la ficha (isCreditLine=true). Antes usábamos heurística por diferencia
  // entre current/available, pero fallaba con conectores que no envían
  // current — la cuenta quedaba clasificada como normal y su available
  // (línea libre) se sumaba al total. Ahora es un toggle explícito.
  const isPoliza = account.isCreditLine;
  const polizaBalance = isPoliza
    ? (account.lastBalanceCurrent !== null && account.lastBalanceCurrent !== 0
        ? account.lastBalanceCurrent
        : account.lastMovementBalance)
    : null;
  let statusNode: JSX.Element;
  if (sapBal === null) {
    statusNode = <span className="meta">sin apuntes SAP</span>;
  } else if (bankBal === null) {
    statusNode = <span className="meta">sin saldo banco</span>;
  } else if (Math.abs(bankBal - sapBal) < 0.01) {
    statusNode = <span className="badge ok" title={`Saldo banco = Saldo SAP (${formatMoney(bankBal, account.currency)})`}>Saldos cuadrados ✓</span>;
  } else {
    const diff = Math.round((bankBal - sapBal) * 100) / 100;
    const diffLabel = `dif ${diff > 0 ? "+" : ""}${diff.toFixed(2)} ${account.currency}`;
    if (account.lastReconciledDate) {
      statusNode = (
        <span
          className="badge warn"
          title={`Los saldos NO cuadran (${diffLabel}). Última fecha conciliada: ${formatDateEs(account.lastReconciledDate)}.`}
        >
          Conciliado hasta {formatDateEs(account.lastReconciledDate)}
        </span>
      );
    } else {
      statusNode = <span className="badge warn" title={diffLabel}>Descuadrado</span>;
    }
  }

  // Color de fondo de la fila: azul claro para cuentas corrientes, verde
  // claro para pólizas de crédito. Sustituye a la columna Tipo eliminada.
  // Los tonos son suaves para no distraer del contenido.
  const rowBg = isPoliza ? "rgba(46,125,50,0.09)" : "rgba(80,160,255,0.07)";
  const rowTooltip = isPoliza
    ? "Póliza de crédito: el saldo relevante es el DISPUESTO."
    : subtypeLabel(account.subtype);

  // Botones uniformes en tamaño para no romper la altura de la fila y no
  // desperdiciar espacio. Etiquetas cortas + tooltip explicativo.
  const btnStyle: React.CSSProperties = {
    padding: "0.15rem 0.5rem",
    minWidth: "2rem",
    height: "1.7rem",
    fontSize: "0.8em",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
  };

  return (
    <tr style={{ background: rowBg, whiteSpace: "nowrap" }} title={rowTooltip}>
      <td><code>{account.bankCode}</code></td>
      <td><code>{formatIban(account.iban)}</code></td>
      <td>{account.alias || <span className="meta">—</span>}</td>
      <td className="num">
        {isPoliza ? (
          polizaBalance !== null ? (
            <>
              <span title="Póliza de crédito: saldo dispuesto (deuda con el banco).">
                {formatMoney(polizaBalance, account.currency)}
              </span>
              {account.lastBalanceAvailable !== null && (
                <div
                  className="meta"
                  style={{ fontSize: "0.75em", marginTop: 1 }}
                  title="Crédito libre que queda por disponer de la línea."
                >
                  disp. {formatMoney(account.lastBalanceAvailable, account.currency)}
                </div>
              )}
            </>
          ) : (
            <span
              className="meta"
              title="La cuenta está marcada como póliza pero WR no envió el saldo dispuesto. Fuerza un refresh para actualizarlo."
            >
              — (refresh)
            </span>
          )
        ) : account.lastBalanceAvailable !== null ? (
          formatMoney(account.lastBalanceAvailable, account.currency)
        ) : (
          <span className="meta">—</span>
        )}
      </td>
      <td className="num">
        {sapBal !== null
          ? formatMoney(sapBal, account.currency)
          : <span className="meta">—</span>}
      </td>
      <td>{statusNode}</td>
      <td>
        {account.lastSyncedAt ? new Date(account.lastSyncedAt).toLocaleDateString() : <span className="meta">nunca</span>}
        {rowError && <div className="error" style={{ fontSize: "0.75em", marginTop: "0.1rem", whiteSpace: "normal" }}>{rowError}</div>}
      </td>
      <td className="actions" style={{ whiteSpace: "nowrap" }}>
        <button type="button" onClick={doRefresh} disabled={busy} title={`Refresca la credencial del banco ${account.bankCode}`} style={btnStyle}>
          {busy ? "…" : "↻"}
        </button>
        <Link to={`/cuentas/${encodeURIComponent(account.iban)}/movimientos`} state={{ from: "/resumen" }}>
          <button type="button" title="Ver movimientos bancarios" style={btnStyle}>Mov.</button>
        </Link>
        <Link to={`/cuentas/${encodeURIComponent(account.iban)}/apuntes-sap`} state={{ from: "/resumen" }}>
          <button type="button" title="Ver apuntes SAP" style={btnStyle}>SAP</button>
        </Link>
        <Link to={`/cuentas/${encodeURIComponent(account.iban)}/conciliar`} state={{ from: "/resumen" }}>
          <button type="button" title="Conciliar movimientos con apuntes SAP" style={btnStyle}>Conc.</button>
        </Link>
      </td>
    </tr>
  );
}
